import { PaginatedList, PaginatedQueryParams } from "../types/pagination";
import { unwrapApiResponse } from "../utils/apiUtils";
import { api, baseUrl } from "./apiSlice";

export type PtiAssetType = "Vehicle" | "Trailer";
export type PtiItemType = "BodyPhoto" | "Tire";
export type PtiStatus = "Submitted" | "Completed" | "Rejected";
export type PtiFluidLevel = "BelowMinimum" | "AtMinimum" | "Good" | "AtMaximum";

export type PtiChecklistScalarValue = boolean | "na" | string;

export type PtiChecklistAnswerValue =
    | PtiChecklistScalarValue
    | { value: boolean | "na"; note?: string };

export type PtiChecklistAnswers = Record<string, PtiChecklistAnswerValue>;

export interface PtiDamageMarkDto {
    zoneKey: string;
    zoneNumber: number;
    note: string;
}

export interface PreTripInspectionItemResponseDto {
    id: number;
    assetType: PtiAssetType;
    itemType: PtiItemType;
    positionKey: string;
    note?: string | null;
    blobName?: string | null;
    originalFileName?: string | null;
    url?: string | null;
    fileSizeBytes?: number | null;
    contentType?: string | null;
    uploadedAt?: string | null;
    treadDepth?: number | null;
    brakeMeasurement?: number | null;
    fluidLevel?: PtiFluidLevel | null;
    tirePsi?: number | null;
    damageMarks?: PtiDamageMarkDto[] | null;
}

export interface PreTripInspectionResponseDto {
    id: number;
    assetType: PtiAssetType;
    vehicleId?: number | null;
    vehicleName?: string | null;
    vehicleUnitNumber?: string | null;
    vehicleVin?: string | null;
    trailerId?: number | null;
    trailerUnitNumber?: string | null;
    trailerVin?: string | null;
    submittedByUserId: number;
    submittedByName: string;
    inspectionDate: string;
    notes?: string | null;
    odometer?: number | null;
    status: PtiStatus;
    createdAt: string;
    updatedAt?: string | null;
    reviewedByUserId?: number | null;
    reviewedByName?: string | null;
    reviewedAt?: string | null;
    rejectionReason?: string | null;
    checklistVersion?: string | null;
    checklistAnswers?: PtiChecklistAnswers | null;
    items: PreTripInspectionItemResponseDto[];
    assetDisplayLabel?: string;
}

export interface PreTripInspectionListItemDto {
    id: number;
    assetType: PtiAssetType;
    vehicleId?: number | null;
    vehicleName?: string | null;
    vehicleUnitNumber?: string | null;
    trailerId?: number | null;
    trailerUnitNumber?: string | null;
    assetDisplayLabel: string;
    submittedByUserId: number;
    submittedByName: string;
    inspectionDate: string;
    notes?: string | null;
    status: PtiStatus;
    photoCount: number;
    totalItemCount: number;
    measurementsComplete: boolean;
    reviewedByUserId?: number | null;
    reviewedByName?: string | null;
    reviewedAt?: string | null;
    rejectionReason?: string | null;
    createdAt: string;
}

export interface UpdatePtiItemMeasurementDto {
    itemType: PtiItemType;
    positionKey: string;
    treadDepth?: number | null;
    brakeMeasurement?: number | null;
    fluidLevel?: PtiFluidLevel | null;
    tirePsi?: number | null;
    damageMarks?: PtiDamageMarkDto[] | null;
}

export interface UpdatePtiItemMeasurementsRequestDto {
    items: UpdatePtiItemMeasurementDto[];
}

export interface RejectPtiInspectionRequestDto {
    reason: string;
}

export type PtiComplianceStatus = "Overdue" | "DueSoon" | "Compliant";

export interface PtiComplianceListItemDto {
    driverUserId: number;
    driverName: string;
    assetType: PtiAssetType;
    assetId: number;
    assetDisplayLabel: string;
    assignmentStartDate: string;
    lastPtiId?: number | null;
    lastPtiDate?: string | null;
    lastPtiStatus?: PtiStatus | null;
    dueAt: string;
    daysOverdue: number;
    hoursUntilDue: number;
    complianceStatus: PtiComplianceStatus;
}

export interface PtiComplianceSummaryDto {
    overdueCount: number;
    dueSoonCount: number;
    compliantCount: number;
    totalCount: number;
}

export interface PtiComplianceParams extends PaginatedQueryParams {
    assetType?: string;
    status?: string;
    driverUserId?: number;
    vehicleId?: number;
    trailerId?: number;
    search?: string;
}

function buildPaginatedParams(queryParams: PaginatedQueryParams) {
    const params: Record<string, string | number> = {
        pageNumber: queryParams.pageNumber,
        pageSize: queryParams.pageSize,
    };

    if (queryParams.filters && Object.keys(queryParams.filters).length > 0) {
        Object.entries(queryParams.filters).forEach(([key, value]) => {
            if (value !== null && value !== undefined && value !== "") {
                params[`filters[${key}]`] = value;
            }
        });
    }

    return params;
}

export const ptiApi = api.injectEndpoints({
    endpoints: (builder) => ({
        getPtiPaginated: builder.query<
            PaginatedList<PreTripInspectionListItemDto>,
            PaginatedQueryParams
        >({
            query: (queryParams) => ({
                url: "pti",
                params: buildPaginatedParams(queryParams),
            }),
            transformResponse: unwrapApiResponse,
            providesTags: ["PtiHistory"],
        }),
        getPtiMinePaginated: builder.query<
            PaginatedList<PreTripInspectionListItemDto>,
            PaginatedQueryParams
        >({
            query: (queryParams) => ({
                url: "pti/mine",
                params: buildPaginatedParams(queryParams),
            }),
            transformResponse: unwrapApiResponse,
            providesTags: ["PtiHistory"],
        }),
        getPtiById: builder.query<PreTripInspectionResponseDto, number>({
            query: (inspectionId) => `pti/${inspectionId}`,
            transformResponse: unwrapApiResponse,
            providesTags: (result, error, inspectionId) => [
                { type: "PtiHistory", id: inspectionId },
                "PtiHistory",
            ],
        }),
        getPtiHistoryByVehicle: builder.query<
            PaginatedList<PreTripInspectionListItemDto>,
            PaginatedQueryParams & { vehicleId: number }
        >({
            query: ({ vehicleId, ...queryParams }) => ({
                url: `vehicles/${vehicleId}/pti`,
                params: buildPaginatedParams(queryParams),
            }),
            providesTags: (result, error, { vehicleId }) => [
                { type: "PtiHistory", id: `vehicle-${vehicleId}` },
                "PtiHistory",
            ],
            transformResponse: unwrapApiResponse,
        }),
        getPtiHistoryByTrailer: builder.query<
            PaginatedList<PreTripInspectionListItemDto>,
            PaginatedQueryParams & { trailerId: number }
        >({
            query: ({ trailerId, ...queryParams }) => ({
                url: `trailers/${trailerId}/pti`,
                params: buildPaginatedParams(queryParams),
            }),
            providesTags: (result, error, { trailerId }) => [
                { type: "PtiHistory", id: `trailer-${trailerId}` },
                "PtiHistory",
            ],
            transformResponse: unwrapApiResponse,
        }),
        updatePtiMeasurements: builder.mutation<
            PreTripInspectionResponseDto,
            { inspectionId: number; body: UpdatePtiItemMeasurementsRequestDto }
        >({
            query: ({ inspectionId, body }) => ({
                url: `pti/${inspectionId}/measurements`,
                method: "PATCH",
                body,
            }),
            transformResponse: unwrapApiResponse,
            invalidatesTags: ["PtiHistory", "PtiCompliance"],
        }),
        approvePti: builder.mutation<PreTripInspectionResponseDto, number>({
            query: (inspectionId) => ({
                url: `pti/${inspectionId}/approve`,
                method: "POST",
            }),
            transformResponse: unwrapApiResponse,
            invalidatesTags: ["PtiHistory", "PtiCompliance"],
        }),
        rejectPti: builder.mutation<
            PreTripInspectionResponseDto,
            { inspectionId: number; body: RejectPtiInspectionRequestDto }
        >({
            query: ({ inspectionId, body }) => ({
                url: `pti/${inspectionId}/reject`,
                method: "POST",
                body,
            }),
            transformResponse: unwrapApiResponse,
            invalidatesTags: ["PtiHistory", "PtiCompliance"],
        }),
        refreshPtiAsset: builder.mutation<PreTripInspectionResponseDto, number>({
            query: (inspectionId) => ({
                url: `pti/${inspectionId}/refresh-asset`,
                method: "POST",
            }),
            transformResponse: unwrapApiResponse,
            invalidatesTags: ["PtiHistory", "PtiCompliance"],
        }),
        getPtiCompliance: builder.query<
            PaginatedList<PtiComplianceListItemDto>,
            PtiComplianceParams
        >({
            query: ({
                pageNumber,
                pageSize,
                assetType,
                status,
                driverUserId,
                vehicleId,
                trailerId,
                search,
            }) => ({
                url: "pti/compliance",
                params: {
                    PageNumber: pageNumber,
                    PageSize: pageSize,
                    ...(assetType && { assetType }),
                    ...(status && { status }),
                    ...(driverUserId && { driverUserId }),
                    ...(vehicleId && { vehicleId }),
                    ...(trailerId && { trailerId }),
                    ...(search && { search }),
                },
            }),
            transformResponse: unwrapApiResponse,
            providesTags: ["PtiCompliance"],
        }),
        getPtiComplianceSummary: builder.query<
            PtiComplianceSummaryDto,
            Omit<PtiComplianceParams, "pageNumber" | "pageSize" | "status">
        >({
            query: ({ assetType, driverUserId, vehicleId, trailerId, search }) => ({
                url: "pti/compliance/summary",
                params: {
                    ...(assetType && { assetType }),
                    ...(driverUserId && { driverUserId }),
                    ...(vehicleId && { vehicleId }),
                    ...(trailerId && { trailerId }),
                    ...(search && { search }),
                },
            }),
            transformResponse: unwrapApiResponse,
            providesTags: ["PtiCompliance"],
        }),
    }),
});

export const {
    useGetPtiPaginatedQuery,
    useGetPtiMinePaginatedQuery,
    useGetPtiByIdQuery,
    useGetPtiHistoryByVehicleQuery,
    useGetPtiHistoryByTrailerQuery,
    useGetPtiComplianceQuery,
    useGetPtiComplianceSummaryQuery,
    useUpdatePtiMeasurementsMutation,
    useApprovePtiMutation,
    useRejectPtiMutation,
    useRefreshPtiAssetMutation,
} = ptiApi;

function triggerBlobDownload(blob: Blob, fileName: string) {
    const url = URL.createObjectURL(blob);
    const anchor = document.createElement("a");
    anchor.href = url;
    anchor.download = fileName;
    document.body.appendChild(anchor);
    anchor.click();
    document.body.removeChild(anchor);
    URL.revokeObjectURL(url);
}

/** Fetches the PTI PDF as a blob (inline preview by default). */
export async function fetchPtiPdfBlob(
    inspectionId: number,
    token: string,
    download = false
): Promise<Blob> {
    const query = download ? "?download=true" : "";
    const response = await fetch(`${baseUrl}/pti/${inspectionId}/pdf${query}`, {
        method: "GET",
        headers: { Authorization: `Bearer ${token}` },
    });

    if (!response.ok) {
        throw new Error(
            `Failed to fetch PTI PDF: ${response.status} ${response.statusText}`
        );
    }

    return response.blob();
}

/** Downloads the PTI PDF and triggers a browser file save. */
export async function downloadPtiPdf(
    inspectionId: number,
    token: string,
    fileName?: string
): Promise<void> {
    const blob = await fetchPtiPdfBlob(inspectionId, token, true);
    triggerBlobDownload(blob, fileName ?? `PTI-${inspectionId}.pdf`);
}
